// POS → Restaurant → Kitchen Display (/pos/rest-kds).
// KDS básico por estación: tickets (cuenta+ronda) con colores por tiempo de
// espera (umbrales por local) y BUMP 100% por teclado — un bump bar físico es
// un teclado USB, el mismo paradigma del POS:
//   ←/→ (o 1-9) selecciona ticket · Espacio/Enter avanza (nuevo→preparando→listo)
//   R = recall del último listo · F = pantalla completa.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChefHat, Maximize2, Minimize2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { SelectorLocal, fieldCls, posRpc } from './shared';
import { EstacionT, ExpoDataT, KdsDataT, RestEstadoT, elementoVisible, restEstadoQuery, useLocalRestaurant } from './restaurante';

export function KdsRestaurant() {
  const qc = useQueryClient();
  const rootRef = useRef<HTMLDivElement>(null);
  const { locationId, setLocationId, locations } = useLocalRestaurant();
  const [modo, setModo] = useState<'estacion' | 'expo'>('estacion');
  const [estacionId, setEstacionId] = useState('');
  const [sel, setSel] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);

  const { data: estado } = useQuery<RestEstadoT>(restEstadoQuery(locationId));
  const estaciones: EstacionT[] = estado?.estaciones ?? [];

  const { data } = useQuery<KdsDataT>({
    queryKey: ['rest-kds', locationId, estacionId],
    queryFn: () => posRpc<KdsDataT>('rest_kds_tickets', { _location: locationId, _estacion: estacionId || null }),
    enabled: !!locationId && modo === 'estacion',
    staleTime: 0, refetchOnMount: 'always', refetchInterval: 3000,
  });
  const { data: expo } = useQuery<ExpoDataT>({
    queryKey: ['rest-expo', locationId],
    queryFn: () => posRpc<ExpoDataT>('rest_expo_tickets', { _location: locationId }),
    enabled: !!locationId && modo === 'expo',
    staleTime: 0, refetchOnMount: 'always', refetchInterval: 3000,
  });

  const tickets = useMemo(() => data?.tickets ?? [], [data]);
  const expoTickets = useMemo(() => expo?.tickets ?? [], [expo]);
  const verde = (data?.umbrales?.verde_min ?? 5) * 60;
  const amarillo = (data?.umbrales?.amarillo_min ?? 8) * 60;

  const avanzar = async (i: number) => {
    const t = tickets[i];
    if (!t) return;
    const siguiente = t.estado === 'comandada' ? 'preparando' : 'lista';
    try {
      await posRpc('rest_kds_set', { _cuenta: t.cuenta_id, _ronda: t.ronda, _estacion: t.estacion_id, _estado: siguiente });
      void qc.invalidateQueries({ queryKey: ['rest-kds'] });
      if (siguiente === 'lista') toast.success(`${t.mesa_numero != null ? `Mesa ${t.mesa_numero}` : t.codigo} ronda ${t.ronda}: LISTA`);
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const recall = async () => {
    const l = (data?.listos ?? [])[0];
    if (!l) { toast.error('No hay tickets listos que recuperar'); return; }
    try {
      await posRpc('rest_kds_set', { _cuenta: l.cuenta_id, _ronda: l.ronda, _estacion: l.estacion_id, _estado: 'preparando' });
      void qc.invalidateQueries({ queryKey: ['rest-kds'] });
      toast.success(`Recall: ${l.mesa_numero != null ? `Mesa ${l.mesa_numero}` : l.codigo} ronda ${l.ronda}`);
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const despachar = async (i: number) => {
    const t = expoTickets[i];
    if (!t) return;
    if (!t.completo) { toast.error('Aún hay estaciones sin terminar'); return; }
    try {
      await posRpc('rest_expo_despachar', { _cuenta: t.cuenta_id, _ronda: t.ronda });
      void qc.invalidateQueries({ queryKey: ['rest-expo'] });
      toast.success(`${t.mesa_numero != null ? `Mesa ${t.mesa_numero}` : t.codigo} R${t.ronda}: SERVIDA`);
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const fire = async () => {
    const r = (expo?.retenidos ?? [])[0];
    if (!r) { toast.error('No hay cursos retenidos'); return; }
    try {
      await posRpc('rest_curso_fire', { _cuenta: r.cuenta_id, _curso: r.curso });
      void qc.invalidateQueries({ queryKey: ['rest-expo'] });
      void qc.invalidateQueries({ queryKey: ['rest-kds'] });
      toast.success(`FIRE: ${r.mesa_numero != null ? `Mesa ${r.mesa_numero}` : r.codigo} curso ${r.curso}`);
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const nTickets = modo === 'expo' ? expoTickets.length : tickets.length;
  useEffect(() => { setSel((s) => Math.min(s, Math.max(nTickets - 1, 0))); }, [nTickets]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (!elementoVisible(rootRef.current)) return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'SELECT') return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); setSel((s) => Math.max(0, s - 1)); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); setSel((s) => Math.min(nTickets - 1, s + 1)); }
      else if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); if (modo === 'expo') void despachar(sel); else void avanzar(sel); }
      else if (/^[1-9]$/.test(e.key)) { e.preventDefault(); const i = Number(e.key) - 1; if (i < nTickets) setSel(i); }
      else if (e.key.toLowerCase() === 'r' && modo === 'estacion') { e.preventDefault(); void recall(); }
      else if (e.key.toLowerCase() === 'c' && modo === 'expo') { e.preventDefault(); void fire(); }
      else if (e.key.toLowerCase() === 'f') {
        e.preventDefault();
        if (document.fullscreenElement) { void document.exitFullscreen(); setFullscreen(false); }
        else { void rootRef.current?.requestFullscreen?.(); setFullscreen(true); }
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets, expoTickets, nTickets, sel, data, expo, modo]);

  const colorEdad = (seg: number) =>
    seg <= verde ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
      : seg <= amarillo ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
      : 'border-red-600 bg-red-50 dark:bg-red-900/25 animate-pulse';
  const mmss = (seg: number) => `${Math.floor(seg / 60)}:${String(Math.floor(seg % 60)).padStart(2, '0')}`;

  return (
    <div ref={rootRef} className="p-4 space-y-3 bg-background min-h-full">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-lg font-semibold flex items-center gap-2"><ChefHat className="w-5 h-5" /> Kitchen Display</h1>
        <SelectorLocal locationId={locationId} setLocationId={setLocationId} locations={locations} />
        <div className="flex rounded-md border overflow-hidden text-sm">
          <button className={`px-3 py-1 ${modo === 'estacion' ? 'bg-primary text-primary-foreground' : ''}`}
                  onClick={() => { setModo('estacion'); setSel(0); }}>Estación</button>
          <button className={`px-3 py-1 ${modo === 'expo' ? 'bg-primary text-primary-foreground' : ''}`}
                  onClick={() => { setModo('expo'); setSel(0); }}>Expedidor</button>
        </div>
        {modo === 'estacion' && (
          <select className={`${fieldCls} !w-auto`} value={estacionId} onChange={(e) => { setEstacionId(e.target.value); setSel(0); }}>
            <option value="">Todas las estaciones</option>
            {estaciones.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
          </select>
        )}
        <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
          <span>{modo === 'expo'
            ? '←/→ o 1-9 elige · Espacio DESPACHA (todo listo) · C fire curso · F pantalla completa'
            : '←/→ o 1-9 elige · Espacio avanza · R recall · F pantalla completa'}</span>
          <Button size="sm" variant="ghost" onClick={() => {
            if (document.fullscreenElement) { void document.exitFullscreen(); setFullscreen(false); }
            else { void rootRef.current?.requestFullscreen?.(); setFullscreen(true); }
          }}>{fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}</Button>
        </div>
      </div>

      {modo === 'expo' && (
        <>
          {(expo?.retenidos ?? []).length > 0 && (
            <div className="flex items-center gap-2 flex-wrap text-sm border border-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded-md p-2">
              <span className="font-medium">Cursos en espera (C = fire del primero):</span>
              {(expo?.retenidos ?? []).map((r) => (
                <button key={`${r.cuenta_id}-${r.curso}`}
                  className="px-2 py-0.5 rounded-full border border-amber-500 text-xs"
                  onClick={async () => {
                    try {
                      await posRpc('rest_curso_fire', { _cuenta: r.cuenta_id, _curso: r.curso });
                      void qc.invalidateQueries({ queryKey: ['rest-expo'] });
                      toast.success(`FIRE curso ${r.curso}`);
                    } catch (e) { toast.error(getErrorMessage(e)); }
                  }}>
                  🔥 {r.mesa_numero != null ? `Mesa ${r.mesa_numero}` : r.codigo} · curso {r.curso} ({r.n})
                </button>
              ))}
            </div>
          )}
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
            {expoTickets.map((t, i) => (
              <button key={`${t.cuenta_id}-${t.ronda}`}
                className={`text-left border-4 rounded-lg overflow-hidden ${t.completo
                  ? 'border-green-600 bg-green-50 dark:bg-green-900/20'
                  : colorEdad(t.edad_seg)} ${i === sel ? 'ring-4 ring-primary' : ''}`}
                onClick={() => { setSel(i); void despachar(i); }}>
                <div className="px-3 py-1.5 flex items-center justify-between font-bold bg-black/5 dark:bg-white/5">
                  <span className="text-lg">{i + 1}. {t.mesa_numero != null ? `MESA ${t.mesa_numero}` : t.tipo.toUpperCase()}</span>
                  <span className="font-mono">{mmss(t.edad_seg)}</span>
                </div>
                <div className="px-3 py-1 text-xs flex gap-2 flex-wrap">
                  {t.estaciones.map((s2) => (
                    <span key={s2.estacion} className={`px-1.5 py-0.5 rounded-full border ${s2.estado === 'lista'
                      ? 'border-green-600 text-green-700 dark:text-green-400'
                      : s2.estado === 'preparando' ? 'border-sky-500 text-sky-700 dark:text-sky-400'
                      : 'border-muted-foreground text-muted-foreground'}`}>
                      {s2.estacion}: {s2.estado} ({s2.n})
                    </span>
                  ))}
                </div>
                <div className="px-3 pb-2 space-y-0.5 text-sm">
                  {t.lineas.map((l) => (
                    <div key={l.id} className={l.estado === 'lista' ? 'text-green-700 dark:text-green-400' : ''}>
                      {Number(l.cantidad)} × {l.descripcion}
                      {(l.modificadores ?? []).length > 0 && (
                        <span className="text-xs text-muted-foreground"> · {(l.modificadores ?? []).map((m2) => m2.nombre).join(', ')}</span>
                      )}
                    </div>
                  ))}
                </div>
                <div className={`px-3 py-1 text-center text-xs font-bold ${t.completo ? 'bg-green-600 text-white' : 'bg-muted text-muted-foreground'}`}>
                  {t.completo ? 'COMPLETO — Espacio para DESPACHAR' : 'esperando estaciones…'}
                </div>
              </button>
            ))}
            {!expoTickets.length && (
              <div className="col-span-full py-16 text-center text-muted-foreground">
                Sin tickets en curso — pase ✔
              </div>
            )}
          </div>
        </>
      )}

      {modo === 'estacion' && (
      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
        {tickets.map((t, i) => (
          <button key={`${t.cuenta_id}-${t.ronda}-${t.estacion_id ?? 'g'}`}
            className={`text-left border-4 rounded-lg overflow-hidden ${colorEdad(t.edad_seg)} ${i === sel ? 'ring-4 ring-primary' : ''}`}
            onClick={() => { setSel(i); void avanzar(i); }}>
            <div className="px-3 py-1.5 flex items-center justify-between font-bold bg-black/5 dark:bg-white/5">
              <span className="text-lg">{i + 1}. {t.mesa_numero != null ? `MESA ${t.mesa_numero}` : t.tipo.toUpperCase()}</span>
              <span className="font-mono">{mmss(t.edad_seg)}</span>
            </div>
            <div className="px-3 py-1 text-xs text-muted-foreground flex justify-between">
              <span>{t.codigo} · R{t.ronda} · {t.estacion}</span>
              <span className={`font-semibold uppercase ${t.estado === 'preparando' ? 'text-sky-600' : ''}`}>{t.estado}</span>
            </div>
            <div className="px-3 pb-2 space-y-1">
              {t.lineas.map((l) => (
                <div key={l.id} className={l.estado === 'anulada' ? 'line-through opacity-50' : ''}>
                  <span className="font-bold text-base">{Number(l.cantidad)} × {l.descripcion}</span>
                  {(l.modificadores ?? []).map((m2, j) => (
                    <div key={j} className="pl-4 text-sm">• {m2.nombre}</div>
                  ))}
                  {l.notas && <div className="pl-4 text-sm font-semibold text-amber-700 dark:text-amber-400">✎ {l.notas}</div>}
                </div>
              ))}
            </div>
            {t.mesero && <div className="px-3 pb-1.5 text-[11px] text-muted-foreground">Mesero: {t.mesero}</div>}
          </button>
        ))}
        {!tickets.length && (
          <div className="col-span-full py-16 text-center text-muted-foreground">
            Sin comandas pendientes — todo al día 👨‍🍳
          </div>
        )}
      </div>
      )}

      {modo === 'estacion' && (data?.listos ?? []).length > 0 && (
        <div className="text-xs text-muted-foreground border-t pt-2">
          Listos recientes (R = recall del más nuevo):{' '}
          {(data?.listos ?? []).map((l) => `${l.mesa_numero != null ? `M${l.mesa_numero}` : l.codigo} R${l.ronda} (${l.estacion})`).join(' · ')}
        </div>
      )}
    </div>
  );
}
